/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { useSnackbar } from 'src/components/snackbar';
import { useBoolean } from 'src/hooks/use-boolean';
import { useAxiosClientContext } from 'src/http/context/axios-client-context';
import { PromotionsRepository } from 'src/repositories/promotions/promotions.repository';
import { normalizeUrl } from 'src/utils/normalize-url';

export type FormValues = {
  promotionName: string;
  startDate: string;
  endDate: string;
  preview: string;
  regulationUrl: string;
  banner: {
    url: string;
    type?: string;
    name?: string;
  };
  thumbnail: {
    url?: string;
    type?: string;
    name?: string;
  };
  stores: string;
};

export const useTableCollapse = (row: any, getData: () => void) => {
  const api = useAxiosClientContext();
  const { toastWarning } = useSnackbar();
  const [hasSuccess, setHasSuccess] = useState(false);
  const useInvalidFile = useBoolean(false);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const defaultValues = useMemo<FormValues>(() => {
    let startDate = '';
    let endDate = '';

    if (row?.startDate && row?.endDate) {
      const start = new Date(row.startDate);
      const end = new Date(row.endDate);
      startDate = start.toISOString().split('T')[0];
      endDate = end.toISOString().split('T')[0];
    }

    return {
      promotionName: row?.promotionName || row?.name || '',
      preview: row?.text || '',
      regulationUrl: row?.regulationUrl || '',
      banner: typeof row?.banner === 'object' && row?.banner !== null ? row.banner : { url: row?.banner ?? '', name: '', type: '' },
      thumbnail:
        typeof row?.thumbnail === 'object' && row?.thumbnail !== null ? row.thumbnail : { url: row?.thumbnail ?? '', name: '', type: '' },
      startDate,
      endDate,
      stores: Array.isArray(row?.stores) ? row.stores.join('\n') : row?.stores || '',
    };
  }, [row]);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        promotionName: Yup.string().required('Campo obrigatório'),
        preview: Yup.string().required('Campo obrigatório'),
        regulationUrl: Yup.string()
          .trim()
          .required('Campo obrigatório')
          .test('valid-url', 'Informe uma URL válida', (value) => !!normalizeUrl(value)),
        startDate: Yup.string().required('Campo obrigatório'),
        endDate: Yup.string().required('Campo obrigatório'),
        banner: Yup.object()
          .shape({
            url: Yup.string().required(),
            type: Yup.string(),
            name: Yup.string(),
          })
          .required('Preencha o campo!'),
        thumbnail: Yup.object()
          .shape({
            url: Yup.string().nullable(),
            type: Yup.string().nullable(),
            name: Yup.string().nullable(),
          })
          .notRequired(),
        // stores: Yup.array().of(Yup.string()).required('Campo obrigatório'),
        stores: Yup.string().required('Campo obrigatório'),
      })
    ),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    setError,

    formState: { isSubmitting, errors },
    watch,
  } = methods;

  const values = watch();

  const onSubmit = handleSubmit(async (data) => {
    const repository = new PromotionsRepository(api);
    const regulationUrl = normalizeUrl(data.regulationUrl);
    if (!regulationUrl) {
      setError('regulationUrl', { type: 'manual', message: 'Informe uma URL válida' });
      return;
    }

    const formattedData = {
      promotionName: data.promotionName,
      preview: data.preview,
      regulationUrl,
      startDate: new Date(data.startDate).toISOString(),
      endDate: new Date(data.endDate).toISOString(),
      banner: data.banner?.url || '',
      thumbnail: data.thumbnail?.url || '',
      stores: data.stores,
      isActive: row.isActive,
    };
    if (data.banner !== row?.banner) {
      const response = await repository.uploadImage(data.banner);
      if (response.isLeft()) {
        console.log(response.value.message);
      } else {
        formattedData.banner = response.value.signedUrl;
      }
    }
    if (data.thumbnail && data.thumbnail.url && data.thumbnail.url !== row?.thumbnail) {
      const response = await repository.uploadImage(data.thumbnail);
      if (response.isLeft()) {
        console.log(response.value.message);
      } else {
        formattedData.thumbnail = response.value.signedUrl;
      }
    }
    if (row!.id === '-') {
      const response = await repository.create(formattedData);
      if (response.isLeft()) {
        toastWarning(response.value.message);
        return;
      }
    } else {
      const response = await repository.update(row!.id, formattedData);
      if (response.isLeft()) {
        toastWarning(response.value.message);
        return;
      }
    }
    await handleSuccess();
    getData();
  });

  const handleSuccess = (): Promise<void> =>
    new Promise((resolve) => {
      setHasSuccess(true);
      setTimeout(() => {
        setHasSuccess(false);
        resolve();
      }, 1000);
    });

  const handleBannerChange = (row: React.ChangeEvent<HTMLInputElement>) => {
    const allowedImageTypes = ['image/svg+xml', 'image/jpeg', 'image/jpg', 'image/png'];
    try {
      const file = row.target.files?.[0];
      if (file) {
        if (!allowedImageTypes.includes(file.type)) {
          useInvalidFile.onTrue();
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const preview = reader.result?.toString();
          const base64 = preview?.split(',')[1] || '';
          setValue('banner', {
            url: base64,
            name: file.name,
            type: file.type,
          });
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      toastWarning('Erro ao selecionar arquivo!');
    }
  };

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allowedImageTypes = ['image/svg+xml', 'image/jpeg', 'image/jpg', 'image/png'];
    try {
      const file = event.target.files?.[0];
      if (file) {
        if (!allowedImageTypes.includes(file.type)) {
          useInvalidFile.onTrue();
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const preview = reader.result?.toString();
          const base64 = preview?.split(',')[1] || '';
          setValue('thumbnail', {
            url: base64,
            name: file.name,
            type: file.type,
          });
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      toastWarning('Erro ao selecionar arquivo!');
    }
  };

  const handleButtonClick = (field: 'banner' | 'thumbnail') => {
    if (field === 'banner') bannerInputRef.current?.click();
    if (field === 'thumbnail') thumbnailInputRef.current?.click();
  };

  function parseDateTime(iso?: string) {
    if (!iso) return { date: '', time: '' };

    const d = new Date(iso);

    const date = d.toISOString().split('T')[0];
    const time = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

    return { date, time };
  }

  useEffect(() => {
    if (row) {
      const { date, time } = parseDateTime(row.date);
      methods.reset({
        ...row,
        banner: typeof row.banner === 'object' && row.banner !== null ? row.banner : { url: row.banner ?? '', name: '', type: '' },
        thumbnail:
          typeof row.thumbnail === 'object' && row.thumbnail !== null ? row.thumbnail : { url: row.thumbnail ?? '', name: '', type: '' },
        regulationUrl: row.regulationUrl || '',
        stores: Array.isArray(row.stores) ? row.stores.join('\n') : row.stores || '',
        date,
        time,
      });
    }
  }, [row, methods]);

  return {
    methods,
    bannerInputRef,
    thumbnailInputRef,
    errors,
    values,
    hasSuccess,
    isSubmitting,
    useInvalidFile,
    handleButtonClick,
    handleBannerChange,
    handleThumbnailChange,
    onSubmit,
  };
};
